import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IRole } from '@core/interfaces/role.interface';
import { Observable, Subject } from 'rxjs';
import { AddCustomRoleDialogComponent } from '../add-custom-role-dialog/add-custom-role-dialog.component';
import { IAddCustomRoleDialogData } from '../add-custom-role-dialog/add-custom-role-dialog.interface';
import { SelectRoleDialogComponent } from './select-role-dialog.component';
import {
  ISelectRoleDialogData,
  ISelectRoleDialogResult,
  TSelectRoleDialogCloseResult,
} from './select-role-dialog.interface';

/**
 * Orchestrates the Select Role dialog together with the "Add custom role" round-trip it can
 * launch. Clicking "Add custom role" inside SelectRoleDialogComponent closes that dialog instance
 * and this service opens Add Custom Role in its place; once that closes, a *new* Select Role
 * dialog instance is opened - with the newly created role selected if one was saved, or with the
 * previous selection intact if the user closed Add Custom Role empty-handed. Each step is a
 * genuinely separate MatDialogRef, so `open()` collapses the whole thing into one Observable that
 * always emits the up-to-date roles list (so the caller can keep its own state in sync even if a
 * role was created along the way) alongside the final chosen role id.
 *
 * `onSave` is threaded through every Select Role instance opened along the way (including after
 * an Add Custom Role detour) - the dialog itself calls it and owns the request lifecycle, see
 * ISelectRoleDialogData's doc comment. `savedRoleId` is threaded through unchanged too, even
 * though `currentRoleId` (the selection seed) gets reset to the newly created role after a
 * detour - it's what SelectRoleDialogComponent's Save compares against to decide whether the
 * selection genuinely differs from what's persisted, so a freshly-created-and-auto-selected role
 * still triggers `onSave` instead of being mistaken for "unchanged".
 */
@Injectable({
  providedIn: 'root',
})
export class SelectRoleDialogService {
  private readonly dialog = inject(MatDialog);

  public open(
    roles: IRole[],
    currentRoleId: string | null,
    onSave: (roleId: string) => Observable<unknown>,
  ): Observable<ISelectRoleDialogResult> {
    const result$ = new Subject<ISelectRoleDialogResult>();

    this.openSelectRole(roles, currentRoleId, currentRoleId, onSave, result$);

    return result$.asObservable();
  }

  private openSelectRole(
    roles: IRole[],
    currentRoleId: string | null,
    savedRoleId: string | null,
    onSave: (roleId: string) => Observable<unknown>,
    result$: Subject<ISelectRoleDialogResult>,
  ): void {
    const dialogRef = this.dialog.open<SelectRoleDialogComponent, ISelectRoleDialogData, TSelectRoleDialogCloseResult>(
      SelectRoleDialogComponent,
      {
        data: { currentRoleId, onSave, roles, savedRoleId } as ISelectRoleDialogData,
        disableClose: true,
        minWidth: '700px',
      },
    );

    dialogRef.afterClosed().subscribe((closeResult) => {
      if (closeResult?.type === 'addCustomRole') {
        this.openAddCustomRole(roles, closeResult.selectedRoleId, savedRoleId, onSave, result$);

        return;
      }

      result$.next({ roleId: closeResult?.type === 'save' ? closeResult.roleId : undefined, roles });
      result$.complete();
    });
  }

  private openAddCustomRole(
    roles: IRole[],
    fallbackRoleId: string | null,
    savedRoleId: string | null,
    onSave: (roleId: string) => Observable<unknown>,
    result$: Subject<ISelectRoleDialogResult>,
  ): void {
    const dialogRef = this.dialog.open<AddCustomRoleDialogComponent, IAddCustomRoleDialogData, IRole | undefined>(
      AddCustomRoleDialogComponent,
      {
        data: { mode: 'create', roles } as IAddCustomRoleDialogData,
        disableClose: true,
        // Explicit `width` (not just `minWidth`) - the permission-toggle grid's flex-wrap content
        // otherwise drives the dialog's auto width well past 700px, unlike Select Role/Team's
        // simpler content.
        width: '700px',
      },
    );

    dialogRef.afterClosed().subscribe((newRole) => {
      const updatedRoles = newRole ? [...roles, newRole] : roles;
      const nextCurrentRoleId = newRole ? newRole.id : fallbackRoleId;

      this.openSelectRole(updatedRoles, nextCurrentRoleId, savedRoleId, onSave, result$);
    });
  }
}
